import { Heart } from "lucide-react";
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { formatINR } from "../lib/format";

const CLOUDINARY_HOST = "res.cloudinary.com";

const optimizeCloudinaryImage = (url, width = 640, height = 800) => {
  if (typeof url !== "string" || !url.includes(CLOUDINARY_HOST)) {
    return url || "";
  }
  return url.replace("/upload/", `/upload/f_auto,q_auto,dpr_auto,c_fill,w_${width},h_${height}/`);
};

export const ProductCard = ({ product, wishlisted, onWishlist, onAddToCart }) => {
  const [mobileBackPreview, setMobileBackPreview] = useState(false);

  const frontImage = useMemo(() => product.artwork_front_url || product.image_urls?.[0] || "", [product]);
  const backImage = useMemo(() => product.artwork_back_url || product.image_urls?.[1] || "", [product]);
  const optimizedFrontImage = useMemo(() => optimizeCloudinaryImage(frontImage), [frontImage]);
  const optimizedBackImage = useMemo(() => optimizeCloudinaryImage(backImage), [backImage]);
  const hasBackImage = Boolean(backImage && backImage !== frontImage);
  const sizeInventory = product.size_inventory || {};
  const preferredSize = useMemo(
    () =>
      product.available_sizes?.find((size) => {
        if (product.preorder) return true;
        if (!sizeInventory || typeof sizeInventory !== "object") return true;
        return Number(sizeInventory[size] ?? 0) > 0;
      }) || product.available_sizes?.[0] || "M",
    [product.available_sizes, product.preorder, sizeInventory],
  );
  const canAddToCart = product.preorder || Number(sizeInventory[preferredSize] ?? product.stock ?? 0) > 0;

  const handleProductClick = (event) => {
    if (!hasBackImage) return;
    if (typeof window === "undefined") return;
    if (window.innerWidth > 1024) return;

    if (!mobileBackPreview) {
      event.preventDefault();
      setMobileBackPreview(true);
    }
  };

  return (
    <article
      className="group overflow-hidden rounded-2xl border border-white/10 bg-[#121212] transition duration-300 hover:-translate-y-1 hover:border-[#bfa46f]/50"
      data-testid={`product-card-${product.slug}`}
    >
      <Link
        to={`/product/${product.slug}`}
        className="block"
        onClick={handleProductClick}
        data-testid={`product-card-link-${product.slug}`}
      >
        <div
          className="relative aspect-[4/5] overflow-hidden bg-[#1c1c1c]"
          onMouseLeave={() => setMobileBackPreview(false)}
          data-testid={`product-card-image-frame-${product.slug}`}
        >
          <img
            src={optimizedFrontImage}
            alt={product.alt_text || product.name}
            className="h-full w-full object-cover object-center transition duration-300 ease-in-out group-hover:scale-[1.03]"
            loading="lazy"
            decoding="async"
            width="640"
            height="800"
            data-testid={`product-card-image-${product.slug}`}
          />
          {hasBackImage && (
            <img
              src={optimizedBackImage}
              alt={`${product.alt_text || product.name} back view`}
              className={`absolute inset-0 h-full w-full object-cover object-center transition duration-300 ease-in-out ${
                mobileBackPreview ? "opacity-100" : "opacity-0 group-hover:opacity-100"
              }`}
              loading="lazy"
              decoding="async"
              width="640"
              height="800"
              data-testid={`product-card-back-image-${product.slug}`}
            />
          )}
          {product.preorder && (
            <span
              className="absolute left-3 top-3 rounded-full bg-[#bfa46f] px-3 py-1 text-[10px] font-semibold uppercase text-black"
              data-testid={`product-preorder-badge-${product.slug}`}
            >
              Preorder
            </span>
          )}
        </div>
      </Link>

      <div className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-[#bfa46f]" data-testid={`product-card-collection-${product.slug}`}>
              {product.collection}
            </p>
            <h3 className="mt-1 text-base text-[#f6f1e7]" data-testid={`product-card-name-${product.slug}`}>
              {product.name}
            </h3>
            <p className="mt-1 text-xs text-[#988d77]" data-testid={`product-card-fit-${product.slug}`}>
              {product.fit_type} fit · {product.gsm} GSM
            </p>
            <p className="mt-1 text-xs text-[#9f957f]" data-testid={`product-card-color-${product.slug}`}>
              {product.base_color}
            </p>
          </div>
          <button
            className={`rounded-full border p-2 transition ${
              wishlisted ? "border-[#bfa46f] bg-[#bfa46f]/20 text-[#f7d797]" : "border-white/15 text-[#ccc] hover:border-[#bfa46f]"
            }`}
            onClick={() => onWishlist(product.id)}
            data-testid={`product-card-wishlist-button-${product.slug}`}
          >
            <Heart size={14} fill={wishlisted ? "currentColor" : "none"} />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <p className="text-sm text-[#f4eee4]" data-testid={`product-card-price-${product.slug}`}>
            {formatINR(product.selling_price)}
          </p>
          <p className="text-xs text-[#7e7564] line-through" data-testid={`product-card-mrp-${product.slug}`}>
            {formatINR(product.mrp)}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Link
            to={`/product/${product.slug}`}
            className="rounded-full border border-white/20 px-3 py-2 text-center text-xs text-[#efe5d5] transition hover:border-[#bfa46f]"
            data-testid={`product-card-quick-view-${product.slug}`}
          >
            Quick View
          </Link>
          <button
            onClick={() => onAddToCart(product, preferredSize)}
            disabled={!canAddToCart}
            className="rounded-full bg-[#bfa46f] px-3 py-2 text-xs font-semibold text-black transition hover:bg-[#ceb98f] disabled:cursor-not-allowed disabled:opacity-55"
            data-testid={`product-card-add-cart-${product.slug}`}
          >
            {canAddToCart ? "Add to Cart" : "Out of Stock"}
          </button>
        </div>
      </div>
    </article>
  );
};
